import { faCircleInfo, faCoins, faUsers } from "@fortawesome/pro-regular-svg-icons"
import { faRightLeft } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, Divider, TextField, Typography } from "@mui/material"
import { IAgency, IAgencyCurrentBalance, IShortAgency } from "agency/model"
import {
  agencyExist,
  canUserAddAgencyBudget,
  getAgencyById,
  getAgencyCurrentBalance,
  getAvailableAgencies
} from "agency/tools/common"
import { translate } from "app/language/service"
import { IRootState } from "app/model"
import { CURRENCIES } from "currency/model"
import { convertValueIntoCurrency } from "currency/tools/common"
import { getCurrencySymbol } from "form/inventory/tools/common"
import { messageSVC } from "message/service"
import { PageLoading } from "page/components/PageLoading"
import { PaymentPopup } from "payment/components/PaymentPopup"
import { IPayment } from "payment/model"
import { checkStripeCustomerId, getAllPaymentForMyAgency } from "payment/tools/agency"
import React, { useEffect, useState } from "react"
import { connect } from "react-redux"
import { ContentBox } from "ui/components/ContentBox"
import { FlexBox } from "ui/components/FlexBox"
import { InfoRow } from "ui/components/InfoRow"
import { LinearLoader } from "ui/components/LinearLoader"
import { AgencyMembers } from "user/components/AgencyMembers"
import { getUserName } from "user/tools/common"
import { ChangeAgencyPopup } from "./ChangeAgencyPopup"
import { MyAgencyPaymentList } from "./MyAgencyPaymentList"

interface IMyAgencyPageProps {
  agency: IAgency | null
  isInitializating: boolean
}

const MyAgencyPageFn = React.memo(({ isInitializating, agency }: IMyAgencyPageProps): JSX.Element => {
  const [isPaymentPopupOpened, setIsPaymentPopupOpened] = React.useState(false)
  const [isChangePopupOpened, setIsChangePopupOpened] = React.useState(false)
  const [amount, setAmount] = useState<number | null>(null)
  const [payments, setPayments] = useState<IPayment[] | null>(null)
  const [agencyBalance, setAgencyBalance] = useState<null | IAgencyCurrentBalance>(null)
  const [isLoadingBalance, setIsLoadingBalance] = useState(false)
  const [agencyBalanceError, setAgencyBalanceError] = useState(false)
  const [listErrorMessage, setListErrorMessage] = useState("")
  const [availableAgencies, setAvailableAgencies] = useState<IShortAgency[]>([])
  const refreshAgencyBalance = () => {
    if (!isLoadingBalance) {
      setIsLoadingBalance(true)
    }
    getAgencyCurrentBalance()
      .then(fetchedAgencyBalance => {
        setAgencyBalance(fetchedAgencyBalance)
        if (agencyBalanceError) {
          setAgencyBalanceError(false)
        }
      })
      .catch(error => {
        console.error(error)
        if (!agencyBalanceError) {
          setAgencyBalanceError(true)
        }
      })
      .finally(() => setIsLoadingBalance(false))
  }
  // triggered on first render only
  useEffect(() => {
    getAllPaymentForMyAgency()
      .then(fetchedPayments => setPayments(fetchedPayments))
      .catch(error => {
        console.error(error)
        setListErrorMessage(translate("agency.all.payment.loading.error"))
      })
    if (agency && !agency.stripe_user_id) {
      checkStripeCustomerId()
    }
  }, [])
  // triggered when agency changed
  useEffect(() => {
    if (agency) {
      refreshAgencyBalance()
      if (!agency.stripe_user_id) {
        checkStripeCustomerId()
      }
      getAvailableAgencies()
        .then(newAvailableAgencies => setAvailableAgencies(newAvailableAgencies.filter(a => a.id !== agency.id)))
        .catch(error => {
          console.error(error)
          messageSVC.error("Unable to check available agencies")
        })
    }
  }, [agency])
  const agencyCurrency = isInitializating || !agency ? CURRENCIES.CAD : (agency.currency.id as CURRENCIES)
  return (
    <FlexBox fullWidth jc="start" vertical fullHeight>
      {isInitializating ? (
        <PageLoading title={translate("label.loading.data")} sx={{ mt: "2rem", mb: "2rem" }} />
      ) : !agency ? (
        <Typography color="error" sx={{ m: "2rem" }}>
          {translate("user.no.agency")}
        </Typography>
      ) : (
        <>
          <FlexBox jc="start" ai="start" fullWidth vertical gap sx={{ p: "1rem", height: "100%", overflow: "auto" }}>
            <FlexBox jc="start" ai="start" fullWidth vertical>
              {availableAgencies.length > 0 && (
                <ContentBox
                  id="agency-change-content-box"
                  icon={faRightLeft}
                  title={translate("label.change")}
                  vertical>
                  {availableAgencies.length > 0 && (
                    <FlexBox gap wrap>
                      <Typography fontSize=".9rem" color="textPrimary">
                        {translate("agency.change.text")}
                      </Typography>
                      <Button onClick={() => setIsChangePopupOpened(true)} variant="contained">
                        {translate("agency.change.button")}
                      </Button>
                    </FlexBox>
                  )}
                </ContentBox>
              )}
              <ContentBox
                id="agency-details-content-box"
                icon={faCircleInfo}
                title={translate("label.details")}
                vertical
                defaultClosed>
                {agency.name && <InfoRow title={translate("label.name")} value={agency.name} />}
                <InfoRow title={translate("label.representative")} value={getUserName(agency.representative)} />
                <InfoRow
                  title={translate("label.account-manager")}
                  value={agency.account_manager ? getUserName(agency.account_manager) : "-"}
                />
                <InfoRow title={translate("label.currency")} value={agency.currency.id} />
              </ContentBox>
              <ContentBox
                id="agency-members-content-box"
                icon={faUsers}
                title={translate("label.members")}
                vertical
                fullWidth
                defaultClosed>
                <AgencyMembers />
              </ContentBox>
              <ContentBox
                id="agency-budget-content-box"
                icon={faCoins}
                title={translate("label.budget")}
                vertical
                fullWidth>
                <FlexBox gap jc="start" wrap fullWidth>
                  {isLoadingBalance ? (
                    <LinearLoader text={translate("label.loading.data")} />
                  ) : agencyBalanceError ? (
                    <Typography color="error">{translate("agency.balance.error")}</Typography>
                  ) : agencyBalance ? (
                    <>
                      <Typography color="textPrimary">{translate("agency.balance")}</Typography>
                      <Typography color="textSecondary">
                        {convertValueIntoCurrency(
                          CURRENCIES.CAD,
                          agency.currency.id as CURRENCIES,
                          agencyBalance.cad_balance
                        ).toFixed(2)}{" "}
                        {getCurrencySymbol(agency.currency.id)}
                      </Typography>
                    </>
                  ) : (
                    <LinearLoader text={translate("label.loading.data")} />
                  )}
                </FlexBox>
                {canUserAddAgencyBudget() ? (
                  <>
                    <Typography color="textPrimary" fontSize=".85rem">
                      {translate("agency.add.budget.help")}
                    </Typography>
                    <FlexBox gap=".5rem" wrap>
                      <TextField
                        label={translate("agency.budget.input")}
                        value={amount === null ? "" : amount}
                        type="number"
                        size="small"
                        onChange={event => {
                          if (!event.target.value) {
                            setAmount(null)
                          } else {
                            setAmount(Number(event.target.value))
                          }
                        }}
                        onKeyUp={event => {
                          event.stopPropagation()
                          event.preventDefault()
                          if (event.key === "Enter" && amount) {
                            setIsPaymentPopupOpened(true)
                          }
                        }}
                      />
                      <Typography color="textPrimary">{agencyCurrency}</Typography>
                      <Button
                        disabled={!amount}
                        variant="contained"
                        onClick={() => setIsPaymentPopupOpened(true)}
                        sx={{ ml: ".5rem" }}>
                        {translate("button.credit")}
                      </Button>
                    </FlexBox>
                  </>
                ) : (
                  <FlexBox gap sx={{ "& .icon": { color: theme => theme.palette.secondary.main } }}>
                    <FontAwesomeIcon icon={faCircleInfo} size="2x" className="icon" />
                    <Typography color="textSecondary" fontSize=".85rem">
                      {translate("agency.budget.disabled.help")}
                    </Typography>
                  </FlexBox>
                )}
                <Divider flexItem />
                <MyAgencyPaymentList payments={payments} errorMessage={listErrorMessage} />
              </ContentBox>
            </FlexBox>
          </FlexBox>
          {isPaymentPopupOpened && amount && (
            <PaymentPopup
              amount={amount}
              currency={agencyCurrency}
              onClose={() => setIsPaymentPopupOpened(false)}
              onSuccess={payment => {
                setPayments([payment, ...(payments as IPayment[])])
                messageSVC.success(translate("agency.payment.success", [amount, agencyCurrency]))
                setIsPaymentPopupOpened(false)
                setAmount(null)
                refreshAgencyBalance()
              }}
            />
          )}
          {isChangePopupOpened && (
            <ChangeAgencyPopup
              currentAgency={agency}
              availableAgencies={availableAgencies}
              onClose={() => setIsChangePopupOpened(false)}
            />
          )}
        </>
      )}
    </FlexBox>
  )
})

export const MyAgencyPage = connect(
  (state: IRootState): IMyAgencyPageProps => ({
    isInitializating: state.app.isInitializating,
    agency: !agencyExist(state.user.user.agency_id, state) ? null : getAgencyById(state.user.user.agency_id, state)
  })
)(MyAgencyPageFn)
